import { filter, FilterOption } from '@order-management/data';
import { formatSalesOrderNumber } from '@order-management/format';
import {
  isTemporary,
  PermissionsGroupName,
  userCanAssignSalesOrderRelatedPermission,
  userCanAssignSalesOrderRelatedPermissionToUser,
} from '@order-management/roles';
import {
  Criteria,
  InputType,
  PaginatedContentConfig,
  SortDirection,
  UserPermissionsCriteria,
  UserSalesOrderPermission,
  UserSalesOrderPermissionTableRow,
} from '@order-management/types';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Columns, Element, Form, Icon } from 'react-bulma-components';
import { FaSort } from 'react-icons/fa6';
import { environment } from '../../../environments/environment';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useUser from '../../shared/hooks/user';
import If from '../../shared/if/if';
import { formatDateToLocaleString } from '../../shared/utils';
import Button from '../../ui/button/button';
import Link from '../../ui/link/link';
import PaginatedTable, {
  CustomPaginatedTableProps,
} from '../../ui/table/paginated-table/paginated-table';
import { Column, OnTableChangeData } from '../../ui/table/types';
import { filterUserPermissions } from './filters';
import styles from './user-permissions-table.module.scss';

export type UserPermissionsTableProps = Omit<
  CustomPaginatedTableProps<UserSalesOrderPermissionTableRow>,
  'config' | 'onChange' | 'empty' | 'form'
> & {
  errorMessage?: ReactNode;
  selectedPermissions: UserSalesOrderPermission[];
  setSelectedPermissions: Dispatch<SetStateAction<UserSalesOrderPermission[]>>;
  isPermissionsUserZspaceInternal: boolean;
};

const INITIAL_ITEMS_PER_PAGE = 10;
const ITEMS_PER_PAGE_OPTIONS = [10, 20, 30, 50];
const DEFAULT_PERMISSION_DAYS_DURATION = 10;
const MAX_PERMISSION_DAYS_DURATION = Number(
  environment.temporaryUserPermissionMaxDaysDuration
);

const filterOptions: FilterOption<UserSalesOrderPermissionTableRow>[] = [
  {
    getter: (el) => formatSalesOrderNumber(el.number),
    searchable: true,
  },
  {
    getter: (el) => el.opportunity?.name ?? '',
    searchable: true,
  },
];

const initialCriteria: UserPermissionsCriteria = {};

export function UserPermissionsTable({
  dataSource,
  errorMessage,
  selectedPermissions,
  setSelectedPermissions,
  isPermissionsUserZspaceInternal,
}: UserPermissionsTableProps) {
  const { user } = useUser();
  const [activeDurationInputRowId, setActiveDurationInputRowId] =
    useState<string>('');
  const [tableCriteria, setTableCriteria] = useState<Criteria>({
    itemsPerPage: INITIAL_ITEMS_PER_PAGE,
    pageNumber: 1,
    search: '',
    sortBy: 'number',
    sortDirection: SortDirection.DES,
  });
  const [filterCriteria, setFilterCriteria] =
    useState<UserPermissionsCriteria>(initialCriteria);

  const isAllowedToEdit = useCallback(
    (
      row: UserSalesOrderPermissionTableRow,
      permission: PermissionsGroupName
    ): boolean => {
      return (
        userCanAssignSalesOrderRelatedPermission(user, row.id, permission) &&
        userCanAssignSalesOrderRelatedPermissionToUser(
          user,
          isPermissionsUserZspaceInternal,
          permission
        )
      );
    },
    [user, isPermissionsUserZspaceInternal]
  );

  const roleOptions = useCallback(
    (
      row: UserSalesOrderPermissionTableRow
    ): {
      label: string;
      value: string;
      disabled: boolean;
    }[] => [
      { label: 'None', value: '', disabled: false },
      {
        label: 'View',
        value: PermissionsGroupName.VIEW_ONLY_SALES_ORDER_MANAGER,
        disabled: !isAllowedToEdit(
          row,
          PermissionsGroupName.VIEW_ONLY_SALES_ORDER_MANAGER as PermissionsGroupName
        ),
      },
      {
        label: 'Manage',
        value: PermissionsGroupName.SALES_ORDER_MANAGER,
        disabled: !isAllowedToEdit(
          row,
          PermissionsGroupName.SALES_ORDER_MANAGER as PermissionsGroupName
        ),
      },
      {
        label: 'Manage - Temporary',
        value: PermissionsGroupName.TEMPORARY_SALES_ORDER_MANAGER,
        disabled: !isAllowedToEdit(
          row,
          PermissionsGroupName.TEMPORARY_SALES_ORDER_MANAGER as PermissionsGroupName
        ),
      },
    ],
    [isAllowedToEdit]
  );

  const tableData = useMemo(() => {
    const userPermissions = dataSource.map((so) => {
      const selectedPermission = selectedPermissions.find(
        (permission) => permission.salesOrderId === so.id
      );
      let permissionsGroup: {
        name: string;
        daysDuration?: number;
      } = {
        name: '',
      };
      if (selectedPermission?.permissionsGroup) {
        permissionsGroup = {
          name: selectedPermission.permissionsGroup.name,
          daysDuration: selectedPermission.permissionsGroup.daysDuration,
        };
      }

      return {
        id: so.id,
        number: so.number,
        opportunity: so.opportunity,
        closeDate: so.closeDate,
        permissionsGroup,
      };
    });
    // Filter by filter bar fields
    const barFilteredData = filterUserPermissions(
      userPermissions,
      filterCriteria
    ) as UserSalesOrderPermissionTableRow[];
    // Filter by table columns
    return filter(barFilteredData, tableCriteria, filterOptions);
  }, [dataSource, filterCriteria, selectedPermissions, tableCriteria]);

  const tablePageData = useMemo(() => {
    return tableData.slice(
      (tableCriteria.pageNumber - 1) * tableCriteria.itemsPerPage,
      tableCriteria.pageNumber * tableCriteria.itemsPerPage
    );
  }, [tableCriteria.itemsPerPage, tableCriteria.pageNumber, tableData]);

  const filtersData = useMemo(() => {
    return {
      salesOrders: {
        title: 'Sales order',
        appliedCount: filterCriteria.salesOrders?.length,
        value: filterCriteria.salesOrders,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrders',
          valueKey: 'salesOrders',
          placeholder: 'Search by SO',
        },
      },
      opportunities: {
        title: 'Opportunity',
        appliedCount: filterCriteria.opportunities?.length,
        value: filterCriteria.opportunities,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'opportunities',
          valueKey: 'opportunities',
          placeholder: 'Search by Opportunity',
        },
      },
    };
  }, [
    filterCriteria.opportunities,
    filterCriteria.salesOrders,
  ]) as FiltersData<UserPermissionsCriteria>;

  const handleTableFilterSubmit = useCallback(
    (value: UserPermissionsCriteria): void => {
      setFilterCriteria(value);
      setTableCriteria((oldCriteria) => ({
        ...oldCriteria,
        pageNumber: 1,
      }));
    },
    []
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof UserPermissionsCriteria)[]) => {
      const newData: UserPermissionsCriteria = { ...filterCriteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newData[filterDataKey];
        } else {
          newData[filterDataKey] = initialFilterValue as string &
            string[] &
            number;
        }
      }
      handleTableFilterSubmit(newData);
    },
    [filterCriteria, handleTableFilterSubmit]
  );

  const appliedFilters = useMemo(() => {
    return Object.entries(filtersData).filter(
      ([_, value]) => value.appliedCount > 0
    );
  }, [filtersData]);

  const tableForm = useMemo(
    () => ({
      debounceMillis: 0,
      itemsPerPageOptions: ITEMS_PER_PAGE_OPTIONS,
      enableSearch: false,
    }),
    []
  );

  const tableConfig = useMemo(() => {
    return {
      ...tableCriteria,
      pages: Math.ceil(tableData.length / tableCriteria.itemsPerPage),
    };
  }, [tableCriteria, tableData.length]);

  const handlePermissionChange = useCallback(
    (permission: UserSalesOrderPermissionTableRow) => {
      const index = selectedPermissions.findIndex(
        (p) => p.salesOrderId === String(permission.id)
      );
      if (index >= 0) {
        if (permission.permissionsGroup.daysDuration === 0) {
          const newSelectedSalesOrderAssignments = selectedPermissions.filter(
            (p) => p.salesOrderId === String(permission.id)
          );
          setSelectedPermissions(newSelectedSalesOrderAssignments);
        } else {
          const newSelectedSalesOrderAssignments = [...selectedPermissions];
          newSelectedSalesOrderAssignments[index].permissionsGroup =
            permission.permissionsGroup;
          setSelectedPermissions(newSelectedSalesOrderAssignments);
        }
      } else {
        const newSelectedSalesOrderAssignments = [
          ...selectedPermissions,
          {
            salesOrderId: permission.id,
            permissionsGroup: permission.permissionsGroup,
          },
        ];
        setSelectedPermissions(newSelectedSalesOrderAssignments);
      }
    },
    [selectedPermissions, setSelectedPermissions]
  );

  const handleTableDataChange = useCallback(
    (value: UserSalesOrderPermissionTableRow) => {
      if (!isTemporary(value.permissionsGroup.name as PermissionsGroupName)) {
        value.permissionsGroup.daysDuration = undefined;
      } else {
        value.permissionsGroup.daysDuration =
          value.permissionsGroup.daysDuration ??
          DEFAULT_PERMISSION_DAYS_DURATION;
      }

      handlePermissionChange(value);
    },
    [handlePermissionChange]
  );

  const resolveDaysDuration = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, rowId: string) => {
      const index = tableData.findIndex((item) => item.id === rowId);
      let newValue = Number(tableData[index].permissionsGroup.daysDuration);
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const mouseY = event.clientY - rect.top;
      const iconHeight = rect.height;
      const relativePosition = mouseY / iconHeight;
      if (relativePosition < 0.5) {
        newValue += 1;
      } else {
        newValue -= 1;
      }
      if (newValue > MAX_PERMISSION_DAYS_DURATION) {
        return MAX_PERMISSION_DAYS_DURATION;
      }
      if (newValue < 1) {
        return 1;
      }
      return newValue;
    },
    [tableData]
  );

  const columns: Column<UserSalesOrderPermissionTableRow>[] = useMemo(
    () => [
      {
        key: 'number',
        className: 'is-width-15',
        render: (el) => (
          <Link to={`/sales-orders/${el.id}`}>
            {formatSalesOrderNumber(el.number)}
          </Link>
        ),
        title: 'Sales Order',
        sortable: true,
      },
      {
        key: 'opportunity.name',
        className: 'is-width-45',
        render: (el) => el.opportunity?.name,
        title: 'Opportunity Name',
        sortable: true,
      },
      {
        key: 'closeDate',
        className: 'is-width-10',
        render: (el) =>
          el.closeDate ? formatDateToLocaleString(el.closeDate) : '',
        title: 'Close date',
        sortable: true,
      },
      {
        key: 'permissions',
        className: 'is-width-30',
        render: (el) => (
          <Columns>
            <Columns.Column size={'half'}>
              <Form.Field alignItems="center">
                <Form.Control>
                  <Form.Select
                    aria-label={`Permissions for ${formatSalesOrderNumber(
                      el.number
                    )}`}
                    fullwidth
                    value={el.permissionsGroup.name}
                    disabled={
                      !isAllowedToEdit(
                        el,
                        el.permissionsGroup.name as PermissionsGroupName
                      )
                    }
                    onChange={(e) => {
                      const updatedElement = el;
                      updatedElement.permissionsGroup.name = e.target.value;
                      handleTableDataChange(updatedElement);
                    }}
                    className={styles.formSelect}
                  >
                    {roleOptions(el).map((option, index) => (
                      <option
                        key={`${option.label}-${index}`}
                        value={option.value}
                        disabled={option.disabled}
                      >
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Control>
              </Form.Field>
            </Columns.Column>
            <Columns.Column
              size={'half'}
              display="flex"
              flexDirection="row"
              alignItems="center"
            >
              <If
                condition={
                  el.permissionsGroup.name ===
                  PermissionsGroupName.TEMPORARY_SALES_ORDER_MANAGER
                }
              >
                <Form.Field className={styles.daysDurationField}>
                  <Form.Control
                    onFocus={() => setActiveDurationInputRowId(el.id)}
                    onBlur={() => setActiveDurationInputRowId('')}
                    onMouseEnter={() => setActiveDurationInputRowId(el.id)}
                    onMouseLeave={() => setActiveDurationInputRowId('')}
                    display="flex"
                    flexDirection="column"
                  >
                    <Form.Input
                      className="is-size-6"
                      name="daysDuration"
                      aria-label={`Days of temporary access for ${formatSalesOrderNumber(
                        el.number
                      )}`}
                      type="number"
                      min={1}
                      max={MAX_PERMISSION_DAYS_DURATION}
                      mr={4}
                      value={
                        el.permissionsGroup.daysDuration ??
                        DEFAULT_PERMISSION_DAYS_DURATION
                      }
                      onChange={(event) => {
                        if (
                          event.target.value !== '' &&
                          Number(event.target.value) < 1
                        ) {
                          event.target.value = '1';
                        }
                        const updatedElement = el;
                        updatedElement.permissionsGroup.daysDuration = Number(
                          event.target.value
                        );
                        handleTableDataChange(updatedElement);
                      }}
                    />

                    <Button
                      className={`is-size-6 ${styles.buttonContainer}`}
                      text
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        const updatedElement = el;
                        updatedElement.permissionsGroup.daysDuration =
                          resolveDaysDuration(event, el.id);
                        handleTableDataChange(updatedElement);
                      }}
                      tabIndex={-1}
                      aria-hidden="true"
                    >
                      <Icon align="right" className={styles.iconContainer}>
                        <FaSort
                          className={
                            activeDurationInputRowId === el.id
                              ? styles.chevronDownActive
                              : styles.chevronDown
                          }
                        />
                      </Icon>
                    </Button>
                  </Form.Control>
                </Form.Field>
                <Element>
                  <span className="is-size-6">days</span>
                </Element>
              </If>
            </Columns.Column>
          </Columns>
        ),
        title: 'Permissions',
        sortable: false,
      },
    ],
    [
      activeDurationInputRowId,
      handleTableDataChange,
      resolveDaysDuration,
      roleOptions,
      isAllowedToEdit,
    ]
  );

  const handleTableChange = (
    value: OnTableChangeData<UserSalesOrderPermissionTableRow>
  ) => {
    const newTableCriteria: PaginatedContentConfig = {
      ...tableCriteria,
      ...value.config,
      sortBy: value.column?.key || tableCriteria.sortBy,
    };

    setTableCriteria(newTableCriteria);
  };

  const emptyContent = useMemo(() => {
    const emptyContentText =
      appliedFilters.length > 0 || tableConfig.search !== ''
        ? 'There are no available permissions that match the selected criteria'
        : 'There are no available permissions at the moment';
    return (
      <Element display="flex" justifyContent="center" className="my-2">
        <span className="my-2 has-text-weight-light">{emptyContentText}</span>
      </Element>
    );
  }, [appliedFilters.length, tableConfig.search]);

  return (
    <>
      <Element display="flex" className="w-full pb-2" alignItems="center">
        <FilterBar
          initialData={initialCriteria}
          data={filterCriteria}
          filtersData={filtersData}
          onFilterSubmit={handleTableFilterSubmit}
          onRemoveFilter={onRemoveFilters}
        />
      </Element>
      <PaginatedTable
        dataSource={tablePageData}
        columns={columns}
        onChange={handleTableChange}
        config={tableConfig}
        form={tableForm}
        empty={emptyContent}
        rowKey={'id'}
        errorMessage={errorMessage}
      />
    </>
  );
}

export default UserPermissionsTable;
